/**
 * addon-eligibility.js — client-side single source of truth for cart add-on
 * eligibility (discreet packaging / expedited shipping).
 *
 * Mirrors snippets/addon-eligibility.liquid. Eligibility = product is a member of the
 * add-on's configured collection (see expedited-discreet-packaging.md §9 & §11).
 *
 * Config is provided by window.ADDON_ELIGIBILITY_CONFIG (rendered in theme.liquid):
 *   { discreet:  { enabled, collection: <handle>, product: <handle> },
 *     expedited: { enabled, collection: <handle>, product: <handle> } }
 *
 * Inert until used: nothing fetches or runs until a consumer calls
 * AddonEligibility.ready(). Safe to load globally with no behavior change.
 */
(function () {
  'use strict';

  var config = window.ADDON_ELIGIBILITY_CONFIG || {};
  var cache = {};          // addon -> Set(variant_id)
  var loadingPromise = null;

  // Fetch all VARIANT ids for the products in a collection, paginated (250/page) to
  // avoid any cap. We index variant ids (not product ids) because both consumers key
  // on variant: cart line items via item.id, and the funnel via finalVariantId.
  async function fetchCollectionVariantIds(handle) {
    var ids = new Set();
    if (!handle) return ids;
    var page = 1;
    while (page <= 20) { // hard safety stop
      var res;
      try {
        res = await fetch('/collections/' + handle + '/products.json?limit=250&page=' + page);
      } catch (e) {
        console.error('[addon-eligibility] fetch failed for collection', handle, e);
        break;
      }
      if (!res.ok) break;
      var data = await res.json();
      var products = (data && data.products) || [];
      products.forEach(function (p) {
        (p.variants || []).forEach(function (v) { ids.add(v.id); });
      });
      if (products.length < 250) break; // last page reached
      page += 1;
    }
    return ids;
  }

  // Build the eligible id sets once; returns a promise. Idempotent.
  function ready() {
    if (loadingPromise) return loadingPromise;
    loadingPromise = Promise.all(
      Object.keys(config).map(function (addon) {
        return fetchCollectionVariantIds(config[addon] && config[addon].collection)
          .then(function (ids) { cache[addon] = ids; });
      })
    ).then(function () { return cache; });
    return loadingPromise;
  }

  function isEligible(variantId, addon) {
    var set = cache[addon];
    return !!set && set.has(parseInt(variantId, 10));
  }

  // True when a line item is a structural line (add-on product itself, or a bundled/
  // attached child) that must not participate in the eligibility decision.
  function isStructural(item) {
    var props = item.properties || {};
    if (props._bundled_product || props._attached_id) return true;
    var gw = config.discreet && config.discreet.product;
    var es = config.expedited && config.expedited.product;
    return item.handle === gw || item.handle === es;
  }

  // Count of eligible boxes for an add-on = the target add-on line-item quantity.
  function eligibleBoxCount(cartItems, addon) {
    var count = 0;
    (cartItems || []).forEach(function (item) {
      if (isStructural(item)) return;
      if (isEligible(item.id, addon)) count += item.quantity;
    });
    return count;
  }

  // Cart-level rule: offer the add-on iff enabled + configured, every real line item
  // is eligible, and there is at least one eligible box. Call after ready() resolves.
  function shouldOffer(cartItems, addon) {
    var cfg = config[addon];
    if (!cfg || !cfg.enabled || !cfg.collection) return false;
    var eligibleCount = 0;
    var items = cartItems || [];
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (isStructural(item)) continue;
      if (isEligible(item.id, addon)) {
        eligibleCount += item.quantity;
      } else {
        return false; // a disallowed real product is present
      }
    }
    return eligibleCount > 0;
  }

  // True if the current cart contains no item ineligible for the add-on (structural
  // lines skipped). Empty cart → true. Used for subscription-page pseudo visibility so we
  // don't offer an add-on that an incompatible cart item would immediately strip.
  function cartAllowsAddon(cartItems, addon) {
    return (cartItems || []).every(function (item) {
      if (isStructural(item)) return true;
      return isEligible(item.id, addon);
    });
  }

  window.AddonEligibility = {
    ready: ready,
    isEligible: isEligible,
    shouldOffer: shouldOffer,
    eligibleBoxCount: eligibleBoxCount,
    cartAllowsAddon: cartAllowsAddon,
    _cache: cache,
    _config: config
  };
})();
